import { HttpClient } from './httpClient'; // Corregir importación a HttpClient (con mayúscula)
import { API_ENDPOINTS } from '../../config/config'; // Usar la configuración correcta

// Definir la interfaz para un objeto Firmware (opcional pero recomendado)
export interface Firmware {
  // ... resto de la interfaz Firmware ...
}

// Definir la interfaz para la respuesta paginada
export interface PaginatedResponse<T> {
  // ... resto de la interfaz PaginatedResponse ...
}

export class FirmwareService {

  /**
   * Obtiene la lista de firmwares disponibles desde la API.
   * @param {object} params - Parámetros de consulta opcionales (limit, skip, sortBy, sortOrder).
   * @returns {Promise<PaginatedResponse<Firmware>>} - La respuesta paginada con los firmwares.
   */
  static async listFirmwares(params: Record<string, any> = {}): Promise<PaginatedResponse<Firmware>> {
    try {
      // Construir los parámetros de consulta
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });

      // Construir la URL completa
      const url = `${API_ENDPOINTS.FIRMWARES}?${searchParams.toString()}`;

      // Llamar a HttpClient.get solo con la URL (o con un objeto vacío si espera opciones)
      // Asumiremos que HttpClient.get(url) funciona.
      // Si requiere un segundo argumento de opciones (e.g., headers), pasarlo aquí.
      const response = await HttpClient.get<PaginatedResponse<Firmware>>(url);

      // Asumiendo que HttpClient devuelve directamente los datos procesados
      return response;
    } catch (error) {
      console.error('Error fetching firmwares:', error);
      // Re-lanzar el error para que el componente lo maneje
      throw error;
    }
  }

  /**
   * Sube un nuevo firmware y registra su versión.
   * @param {FormData} formData - Los datos del formulario (version, description?, firmwareFile).
   * @returns {Promise<Firmware>} - El registro del firmware creado.
   */
  static async createFirmware(
    formData: FormData
  ): Promise<Firmware> {
    try {
      // Llamar a HttpClient.post pasando solo URL y FormData
      const response = await HttpClient.post<Firmware>(
        API_ENDPOINTS.FIRMWARES,
        formData,
        { // Quitar el objeto de opciones o dejarlo vacío si es necesario
          headers: {
            // No establecer Content-Type aquí, HttpClient lo manejará
          },
          // Quitar onUploadProgress
        }
      );
      // El tipo devuelto por post ya debería ser Firmware o null
      // Necesitamos asegurar que devolvemos Firmware como promete la firma
      if (!response) {
        // Esto podría pasar si la API devuelve 204 u otra respuesta vacía inesperadamente
        throw new Error('La API no devolvió datos del firmware creado.');
      }
      return response;
    } catch (error) {
      console.error('Error creating/uploading firmware:', error);
      throw error;
    }
  }

  /**
   * Elimina un registro de firmware y su archivo asociado.
   * @param {string} id - ID del firmware a eliminar.
   * @returns {Promise<void>} - No devuelve nada en caso de éxito.
   */
  static async deleteFirmware(id: string): Promise<void> {
    try {
      // Llamar a HttpClient.delete
      await HttpClient.delete<void>(
        `${API_ENDPOINTS.FIRMWARES}/${id}`
      );
      // No devuelve nada en éxito (204 No Content)
    } catch (error) {
      console.error(`Error deleting firmware ${id}:`, error);
      throw error;
    }
  }

  /**
   * Actualiza campos de texto de un firmware existente.
   */
  static async updateFirmware(
    id: string,
    data: { name?: string; description?: string; version?: string }
  ): Promise<Firmware> {
    try {
      const response = await HttpClient.patch<Firmware>(
        `${API_ENDPOINTS.FIRMWARES}/${id}`,
        data
      );
      return response;
    } catch (error) {
      console.error(`Error updating firmware ${id}:`, error);
      throw error;
    }
  }
}
